/**
 * `VersionVorschauDrawer` — Read-Only-Vorschau einer archivierten Angebotsversion
 * (B2, UX-Polish 25.08.2026). Extrahiert aus `pages/event-detail.tsx` (WP0, Auftrags-Dashboard-
 * Fundament, 08.09.2026) — reines Refactoring, identisches Markup/Verhalten wie zuvor inline.
 *
 * Das Versionsarchiv war eine Liste, die NICHT sagt, was in einer Version stand. Wer wissen
 * wollte, ob die alte Fassung die bessere war, musste sie kopieren — und hatte danach eine
 * zusaetzliche Variante im Builder, die er wieder wegraeumen musste. Diese Ansicht beantwortet
 * die Frage, ohne etwas anzulegen: sie liest ausschliesslich den eingefrorenen Snapshot
 * (`offer_history.options_snapshot`), es gibt kein Eingabefeld und keinen Speichern-Weg.
 *
 * Erst der Knopf im Fuss legt etwas an — append-only ueber den bestehenden `restore`-Pfad
 * (`POST /api/events/:id/offer-history/:version/restore`, ausgeloest ueber `onRestore`): die
 * bestehenden Varianten bleiben stehen, es kommt eine dazu. Deshalb heisst er „Als Basis
 * verwenden" und nicht „wiederherstellen"; wiederhergestellt wird nichts, ueberschrieben erst
 * recht nicht. Die eigentliche Mutation (inkl. Fehlertext-Uebersetzung) bleibt bewusst beim
 * Aufrufer (`event-detail.tsx`), der auch `historyQ`/`offersQ` kennt — diese Komponente ist reine
 * Darstellung + ein Callback.
 */
import { Drawer, Icon, Button } from "../components";
import { formatDate, money } from "../format";
import type { EventStatus } from "../types";

/** Versand-Snapshot (offer_history) — items/days nur ab Block 3 vorhanden (aeltere Versionen: Summary-only). */
/**
 * B2 (UX-Polish 25.08.2026): Die Positionen waren bis hierher `unknown[]` — es reichte, ihre
 * ANWESENHEIT zu pruefen (`hasItems`), weil sie niemand anzeigte. Die Read-Only-Vorschau zeigt
 * sie jetzt, also brauchen sie eine Form. Sie spiegelt `snapshotItemSchema` aus
 * `apps/api/src/routes/offer-items.ts` — die Zod-Form, gegen die der `restore`-Pfad denselben
 * Snapshot liest. Alles, was dort optional ist, ist es hier auch: aeltere Snapshots (vor
 * Migration 220) tragen einzelne Felder schlicht nicht, und eine Vorschau darf daran nicht
 * zerbrechen. Fremdes jsonb bleibt fremdes jsonb — deshalb wird unten defensiv gelesen.
 */
export interface OfferHistoryItemSnapshot {
  section?: string;
  name?: string;
  courseLabel?: string | null;
  dayNo?: number | null;
  qtyMilli?: number;
  unit?: string | null;
  unitPriceCents?: number;
  priceMode?: string;
  isOptional?: boolean;
}
export interface OfferHistoryOptionSnapshot {
  label: string;
  title: string | null;
  guestCount: number;
  amountTotalCents: number;
  items?: OfferHistoryItemSnapshot[];
  days?: unknown[];
}
export interface OfferHistoryEntry {
  id: string;
  version: number;
  sentAt: string | null;
  optionsSnapshot: OfferHistoryOptionSnapshot[];
}

/**
 * B2: Abschnittsnamen fuer die Vorschau. Bewusst eine eigene, kleine Tabelle statt eines Imports
 * aus `pages/builder.tsx` — dort ist `SECTIONS` ein Array mit Icon und Reihenfolge fuer den
 * EDITOR, und ein Import quer zwischen zwei grossen Seiten zoege die halbe Builder-Datei in diese
 * hinein. Unbekannte Schluessel (aeltere Snapshots, spaeter ergaenzte Abschnitte) fallen auf den
 * Rohwert zurueck, statt zu verschwinden.
 */
export const SEKTIONS_LABEL: Record<string, string> = {
  menu: "Menü",
  drinks: "Getränke",
  equipment: "Equipment",
  staff: "Personal",
  other: "Sonstiges",
  adjustment: "Anpassung",
};

/**
 * B2: „12 × 45,00 €" bzw. „45,00 €/Gast" — die Mengenangabe einer archivierten Position.
 *
 * `qtyMilli` ist Tausendstel (1000 = 1 Stueck), `priceMode` unterscheidet Pauschale von
 * Pro-Person-Preis. Beides wird DEFENSIV gelesen: der Snapshot ist jsonb, aeltere Fassungen
 * tragen einzelne Felder gar nicht, und eine Vorschau darf daran nicht zerbrechen — im Zweifel
 * steht dort weniger, aber nie „NaN".
 *
 * Bewusst OHNE `@maestro/pricing`: die Engine rechnet Summen fuer LEBENDE Angebote. Der Snapshot
 * traegt seine Summe bereits fertig (`amountTotalCents`, oben angezeigt) — hier nachzurechnen
 * hiesse, eine zweite Wahrheit neben die eingefrorene zu stellen.
 */
export function mengeUndPreis(it: OfferHistoryItemSnapshot | undefined, gaeste: number): string {
  if (!it) return "";
  const preis = typeof it.unitPriceCents === "number" ? money(it.unitPriceCents) : null;
  if (preis == null) return "";
  if (it.priceMode === "per_person") return `${preis}/Gast${gaeste > 0 ? ` × ${gaeste}` : ""}`;
  const menge = typeof it.qtyMilli === "number" ? it.qtyMilli / 1000 : null;
  if (menge == null || menge === 1) return preis;
  const mengeText = Number.isInteger(menge) ? String(menge) : menge.toLocaleString("de-DE", { maximumFractionDigits: 2 });
  return `${mengeText} × ${preis}`;
}

export interface VersionVorschauDrawerProps {
  /** Eintrag aus `GET /api/events/:id/offer-history`, oder `null` — dann wird nichts gerendert
   *  (identisch zur fruehreren Inline-IIFE in `event-detail.tsx`, die bei fehlendem Eintrag
   *  ebenfalls `null` zurueckgab). */
  eintrag: OfferHistoryEntry | null;
  /** Fuer die Sperr-Pruefung unten (Spiegel von `OFFER_LOCKED_STATUSES`). */
  eventStatus: EventStatus;
  /** Version, fuer die gerade "Als Basis verwenden" laeuft (Busy-Text am Knopf). */
  historyBusy: number | null;
  onClose: () => void;
  onRestore: (version: number) => void | Promise<void>;
}

export function VersionVorschauDrawer({ eintrag, eventStatus, historyBusy, onClose, onRestore }: VersionVorschauDrawerProps) {
  if (!eintrag) return null;
  const vollstaendig = eintrag.optionsSnapshot.every((o) => Array.isArray(o.items) && Array.isArray(o.days));
  const gesamt = eintrag.optionsSnapshot.reduce((sum, o) => sum + (o.amountTotalCents ?? 0), 0);
  /**
   * DIE SPERRE, SAUBER STATT ALS 409 (Auftrag B2, ausdrueckliche Auflage).
   *
   * `restore` steht seit dem Guard-Paket unter `eventOfferLocked` (`routes/offer-items.ts`,
   * belegt in `apps/api/test/offer-lock.test.ts`): nach der verbindlichen Annahme legt es
   * keine zusaetzliche Variante mehr an, weil `verbindlicheVariante()` sonst mehrdeutig
   * wuerde — Rechnung UND Kuechenzettel antworten danach dauerhaft mit `no_chosen_option`.
   * Genau dieser EINE Klick aus dem Versionsarchiv hat einen gewonnenen Auftrag blockiert
   * (Bughunt § 12.1). Der Server lehnt das jetzt ab; die Oberflaeche muss es VORHER sagen,
   * sonst klickt der Betreiber und bekommt einen Fehlercode als Antwort auf eine
   * vernuenftige Frage.
   *
   * Die Statusliste spiegelt `OFFER_LOCKED_STATUSES` (`apps/api/src/lib/offer-lock.ts`)
   * wortgleich. Der Server bleibt die Wahrheit — der `event_locked`-Fall wird vom Aufrufer
   * (`restoreVersion()` in `event-detail.tsx`) zusaetzlich uebersetzt, falls sich der Status
   * zwischen Laden und Klick aendert.
   */
  const gesperrt = ["offer_chosen", "paid", "payment_failed", "balance_due", "completed"].includes(eventStatus);
  const sperrGrund = !vollstaendig
    ? "Diese Version wurde vor der Positions-Archivierung versendet — sie enthält keine Positionen zum Übernehmen."
    : gesperrt
      ? "Das Angebot ist seit der verbindlichen Annahme gesperrt. Eine zusätzliche Variante würde die angenommene Fassung mehrdeutig machen."
      : null;
  return (
    <Drawer
      open
      onClose={onClose}
      breite="breit"
      title={`Version v${eintrag.version}`}
      subtitle={`${eintrag.sentAt ? `Gesendet am ${formatDate(eintrag.sentAt)}` : "Ohne Versanddatum"} · ${
        eintrag.optionsSnapshot.length
      } ${eintrag.optionsSnapshot.length === 1 ? "Variante" : "Varianten"} · ${money(gesamt)}`}
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose}>
            Schließen
          </Button>
          <Button
            type="button"
            data-testid="versionsarchiv-als-basis"
            className="disabled:cursor-not-allowed disabled:opacity-45"
            disabled={sperrGrund != null || historyBusy === eintrag.version}
            title={sperrGrund ?? "Legt eine zusätzliche Variante mit diesem Stand an — bestehende bleiben unberührt"}
            onClick={() => void onRestore(eintrag.version)}
          >
            <Icon name="content_copy" className="text-[16px]" />{" "}
            {historyBusy === eintrag.version ? "Wird kopiert …" : "Als Basis verwenden"}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        {/* Die Begruendung steht IM Bild, nicht nur im title-Attribut: ein ausgegrauter
            Knopf ohne Erklaerung ist dieselbe Sackgasse wie ein 409, nur leiser. */}
        {sperrGrund && (
          <p
            data-testid="versionsarchiv-sperrgrund"
            className="flex items-start gap-2 rounded-xl border border-secondary/40 bg-[#F0EAE0] px-3 py-2.5 font-body-sm text-[12px] text-secondary dark:border-white/10 dark:bg-white/[0.08] dark:text-[#b8ae9e]"
          >
            <Icon name="lock" className="mt-0.5 shrink-0 text-[15px]" />
            <span>{sperrGrund}</span>
          </p>
        )}

        <p className="font-body-sm text-[12px] text-secondary dark:text-secondary-fixed-dim">
          Nur zum Ansehen — dieser Stand ist eingefroren und ändert sich nicht mehr.
        </p>

        {eintrag.optionsSnapshot.map((o, oi) => {
          const positionen = Array.isArray(o.items) ? o.items : [];
          const proGast = o.guestCount > 0 ? Math.round((o.amountTotalCents ?? 0) / o.guestCount) : null;
          // Nach Abschnitt gruppiert, in der Reihenfolge ihres ersten Auftretens — dieselbe
          // Ordnung, in der der Snapshot sie traegt (Server sortiert nach section/sortOrder).
          const nachAbschnitt = new Map<string, OfferHistoryItemSnapshot[]>();
          for (const it of positionen) {
            const key = typeof it?.section === "string" ? it.section : "sonstiges";
            nachAbschnitt.set(key, [...(nachAbschnitt.get(key) ?? []), it]);
          }
          return (
            <div key={`${o.label}-${oi}`} className="rounded-xl border border-outline-variant/40 p-4 dark:border-outline/30">
              <div className="flex items-baseline justify-between gap-3">
                <h3 className="min-w-0 truncate font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">
                  {o.title || `Variante ${o.label}`}
                </h3>
                <span className="shrink-0 font-body-sm text-body-sm font-bold text-on-background dark:text-inverse-on-surface">
                  {money(o.amountTotalCents ?? 0)}
                </span>
              </div>
              <p className="mt-0.5 font-body-sm text-[11px] text-secondary dark:text-secondary-fixed-dim">
                {o.guestCount} Gäste{proGast != null ? ` · ${money(proGast)} pro Gast` : ""}
                {positionen.length > 0 ? ` · ${positionen.length} ${positionen.length === 1 ? "Position" : "Positionen"}` : ""}
              </p>

              {positionen.length === 0 ? (
                <p className="mt-3 font-body-sm text-[11.5px] text-on-surface-variant/70">
                  Für diese Version sind keine Positionen archiviert — nur die Summe oben.
                </p>
              ) : (
                <div className="mt-3 space-y-3">
                  {[...nachAbschnitt.entries()].map(([abschnitt, zeilen]) => (
                    <div key={abschnitt}>
                      <p className="font-label-md text-[10.5px] uppercase tracking-wide text-secondary-fixed-dim">
                        {SEKTIONS_LABEL[abschnitt] ?? abschnitt}
                      </p>
                      <ul className="mt-1 space-y-1">
                        {zeilen.map((it, ii) => (
                          <li key={`${it?.name ?? "?"}-${ii}`} className="flex items-baseline justify-between gap-3 font-body-sm text-[12px]">
                            <span className="min-w-0 text-on-background dark:text-inverse-on-surface">
                              <span className="truncate">{it?.name ?? "(ohne Namen)"}</span>
                              {it?.isOptional && (
                                <span className="ml-1.5 text-[10.5px] text-secondary-fixed-dim">optional</span>
                              )}
                            </span>
                            <span className="shrink-0 text-secondary dark:text-secondary-fixed-dim">
                              {mengeUndPreis(it, o.guestCount)}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Drawer>
  );
}
